import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const RESERVED = new Set(['admin','login','cadastro','painel','api','app','loja','lojas','suporte','ajuda','termos','privacidade','contato','sobre','planos','www','e-ambulante','ambulante','fornecedor','cliente','checkout','carrinho','pedido','pedidos','static','assets','porra','caralho','merda']);
export function normalizeSlug(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 30).replace(/-+$/g, '');
}
export function validateSlug(slug: string): string | null {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) || slug.length < 3 || slug.length > 30) return 'Use 3 a 30 letras, números ou hífens.';
  if (RESERVED.has(slug)) return 'Esse endereço é reservado.';
  return null;
}
export function slugCandidates(name: string, city?: string): string[] {
  const base = normalizeSlug(name) || 'loja';
  const local = normalizeSlug(city || '');
  return [base, ...(local ? [`${base}-${local}`] : [])].map((s) => normalizeSlug(s));
}

export async function claimSlug(uid: string, name: string, city?: string, requested?: string): Promise<string> {
  const first = requested ? normalizeSlug(requested) : '';
  if (requested && validateSlug(first)) throw new Error(validateSlug(first)!);
  const candidates = first ? [first] : slugCandidates(name, city);
  const base = candidates[candidates.length - 1];
  for (let i = 0; i < 100; i++) {
    const slug = i === 0 ? candidates[0] : i === 1 && candidates[1] ? candidates[1] : `${base}-${i}`;
    const ref = doc(db, 'slugs', slug);
    try {
      return await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (snap.exists() && snap.data()?.uid !== uid) throw new Error('used');
        tx.set(ref, { uid, ativo: true, criadoEm: snap.exists() ? snap.data()?.criadoEm || serverTimestamp() : serverTimestamp() });
        return slug;
      });
    } catch (e) {
      if (!(e instanceof Error && e.message === 'used')) throw e;
    }
  }
  throw new Error('Não foi possível encontrar um endereço disponível.');
}

export async function changeStoreSlug(uid: string, oldSlug: string | undefined, name: string, city: string | undefined, requested: string, lastChanged?: unknown): Promise<string> {
  const next = await claimSlug(uid, name, city, requested);
  if (next === oldSlug) return next;
  if (lastChanged && Date.now() - new Date(lastChanged as string).getTime() < 30 * 86400000) throw new Error('O link só pode ser trocado uma vez a cada 30 dias.');
  if (oldSlug) {
    await runTransaction(db, async (tx) => tx.set(doc(db, 'slugs', oldSlug), { uid, ativo: false, redirectTo: next }, { merge: true }));
  }
  return next;
}

export async function resolveStoreSlug(raw: string): Promise<{ uid: string; slug: string; kind: 'vendor' | 'supplier' } | null> {
  const slug = normalizeSlug(raw);
  const snap = await getDoc(doc(db, 'slugs', slug));
  if (!snap.exists()) return null;
  const data = snap.data() as { uid: string; ativo?: boolean; redirectTo?: string };
  const resolvedSlug = data.ativo === false && data.redirectTo ? data.redirectTo : slug;
  const uid = data.uid;
  const [v, s] = await Promise.all([
    getDoc(doc(db, 'vendors', uid)),
    getDoc(doc(db, 'suppliers', uid)),
  ]);
  // Se não existir em nenhuma das duas coleções, trata como loja inexistente (fallback).
  if (!v.exists() && !s.exists()) return null;
  const kind: 'vendor' | 'supplier' = v.exists() ? 'vendor' : 'supplier';
  return { uid, slug: resolvedSlug, kind };
}

/** Libera o slug do dono quando o cadastro falha após a reserva (rollback). */
export async function releaseSlug(uid: string, slug: string): Promise<void> {
  const s = normalizeSlug(slug);
  if (!s) return;
  const ref = doc(db, 'slugs', s);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    if (String(snap.data()?.uid) === uid) tx.delete(ref);
  });
}
